/**
 * TrackerPage — Route-level page for a SmartBlock tracker.
 * Reads tracker_type_id from URL search params and renders the tracker UI.
 * Used by both prospect and coach locker room routes.
 */
import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SmartBlockTracker } from '@/components/smartblocks/SmartBlockTracker';
import { CoachAthleteOverview } from '@/components/smartblocks/CoachAthleteOverview';
import { useAuthContext } from '@/lib/auth-context';

type TrackerPageProps = {
  /** Base path to navigate back (e.g., /portal/locker/tools) */
  backPath: string;
  /** Role for the tracker context */
  role: 'prospect' | 'coach' | 'student';
};

export function TrackerPage({ backPath, role }: TrackerPageProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const trackerTypeId = searchParams.get('type');

  if (!trackerTypeId) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-600">No tracker type specified.</p>
          <button
            onClick={() => navigate(backPath)}
            className="mt-3 rounded-lg bg-red-100 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-200"
          >
            Back to Tools
          </button>
        </div>
      </div>
    );
  }

  const isCoach = role === 'coach';
  const [viewMode, setViewMode] = React.useState<'personal' | 'roster'>(
    isCoach ? 'roster' : 'personal',
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      {/* Back link */}
      <button
        onClick={() => navigate(backPath)}
        className="mb-4 flex items-center gap-1 text-sm text-cocoa/60 hover:text-cocoa transition-colors"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Tools
      </button>

      {/* Coach can toggle between personal and roster view */}
      {isCoach && (
        <div className="mb-4 flex gap-1 rounded-lg bg-slate-100 p-1">
          <button
            onClick={() => setViewMode('roster')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'roster'
                ? 'bg-white text-cocoa shadow-sm'
                : 'text-cocoa/60 hover:text-cocoa'
            }`}
          >
            Athlete Roster
          </button>
          <button
            onClick={() => setViewMode('personal')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'personal'
                ? 'bg-white text-cocoa shadow-sm'
                : 'text-cocoa/60 hover:text-cocoa'
            }`}
          >
            My Tracker
          </button>
        </div>
      )}

      {/* Content */}
      {viewMode === 'roster' && isCoach ? (
        <CoachAthleteOverview trackerTypeId={trackerTypeId} />
      ) : (
        <SmartBlockTracker trackerTypeId={trackerTypeId} role={role} />
      )}
    </div>
  );
}

export default TrackerPage;
